import React from "react";
import { Form, Input, Button, Flex, theme, type FormInstance, Select, InputNumber } from "antd";
import { CheckCircleFilled, FileTextOutlined, NumberOutlined, PartitionOutlined } from "@ant-design/icons";
import { SectionHeader, FieldLabel, inputStyle } from "../../shared";
import type { ChartAccountData } from "../types";

interface ChartAccountFormProps {
  form: FormInstance<ChartAccountData>;
  onFinish: (values: ChartAccountData) => void;
  isPending: boolean;
  buttonText: string;
  formId?: string;
}

const ChartAccountForm: React.FC<ChartAccountFormProps> = ({ form, onFinish, isPending, buttonText, formId = "chart-account-form" }) => {
  const { token } = theme.useToken();
  const iStyle = inputStyle(token);

  const handleFinish = (values: any) => {
    onFinish({
      ...values,
      grouping_code: Number(values.grouping_code),
      level: Number(values.level),
    });
  };

  return (
    <Form
      id={formId}
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      requiredMark={false}
      style={{ width: "100%" }}
    >
      <Flex vertical gap={16} style={{ marginBottom: 28 }}>
        <SectionHeader title="Datos de la Cuenta" />

        <Form.Item
          name="account_name"
          label={<FieldLabel text="Nombre de la Cuenta" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<FileTextOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. Bancos Nacionales"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="grouping_code"
          label={<FieldLabel text="Código Agrupador" />}
          rules={[
            { required: true, message: "Campo requerido" },
            { type: "number", message: "Debe ser un número", transform: (v) => v !== undefined && v !== null ? Number(v) : v }
          ]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber
            size="large"
            prefix={<NumberOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. 102"
            style={{ ...iStyle, width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          name="level"
          label={<FieldLabel text="Nivel" />}
          rules={[
            { required: true, message: "Campo requerido" },
            { type: "number", message: "Debe ser un número", transform: (v) => v !== undefined && v !== null ? Number(v) : v }
          ]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber
            size="large"
            prefix={<PartitionOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. 1"
            style={{ ...iStyle, width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          name="type_account"
          label={<FieldLabel text="Tipo de Cuenta" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 12 }}
        >
          <Select
            size="large"
            placeholder="Seleccionar"
            style={iStyle}
            options={[
              { value: "Deudora", label: "Deudora" },
              { value: "Acreedora", label: "Acreedora" },
              { value: "Mixta", label: "Mixta" },
            ]}
          />
        </Form.Item>
      </Flex>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        loading={isPending}
        icon={<CheckCircleFilled />}
        block
        style={{
          height: 54,
          borderRadius: token.borderRadiusLG,
          fontWeight: 800,
          fontSize: 16,
          letterSpacing: "0.01em",
          boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
        }}
      >
        {buttonText}
      </Button>
    </Form>
  );
};

export default ChartAccountForm;
